// src/components/blog/CommentSection.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiMessageCircle,
  FiThumbsUp,
  FiSend,
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiShield,
  FiCornerDownRight,
  FiCopy,
  FiCheck,
  FiSmile,
  FiHeart,
  FiFrown,
  FiThumbsDown,
  FiX,
} from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const REACTIONS = {
  like: { icon: FiThumbsUp, label: 'Like', color: '#3b82f6', emoji: '👍' },
  love: { icon: FiHeart, label: 'Love', color: '#ef4444', emoji: '❤️' },
  haha: { icon: FiSmile, label: 'Haha', color: '#f59e0b', emoji: '😂' },
  sad: { icon: FiFrown, label: 'Sad', color: '#8b5cf6', emoji: '😢' },
  angry: { icon: FiThumbsDown, label: 'Angry', color: '#ef4444', emoji: '😠' },
};

const ADMIN_NAME = 'Wd Hejbul';
const ADMIN_AVATAR =
  'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1778039977/Fabicon-icon-of-wdhejbul_zrboh7.jpg';

// ============================================
// HELPER FUNCTIONS
// ============================================

const generateUserIdentifier = async email => {
  const data = email.toLowerCase().trim();
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// ============================================
// TOAST NOTIFICATION COMPONENT
// ============================================

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-500 text-white',
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-[10000] px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${styles[type]}`}
    >
      {type === 'success' && <FiCheck className="w-5 h-5" />}
      {type === 'error' && <FiAlertCircle className="w-5 h-5" />}
      {type === 'warning' && <FiAlertCircle className="w-5 h-5" />}
      {type === 'info' && <FiMessageCircle className="w-5 h-5" />}
      <span className="text-sm">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70">
        <FiX className="w-4 h-4" />
      </button>
    </div>
  );
};

// ============================================
// LOADING SPINNER COMPONENT
// ============================================

const LoadingSpinner = ({ size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-8 h-8', lg: 'w-12 h-12' };
  return (
    <div className="flex justify-center items-center">
      <div
        className={`${sizes[size]} animate-spin rounded-full border-2 border-gray-300 border-t-blue-500`}
      />
    </div>
  );
};

// ============================================
// REACTION POPUP COMPONENT
// ============================================

const ReactionPopup = ({ position, onReactionSelect, onClose, isReacting }) => {
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleScroll = () => onClose();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [onClose]);

  return (
    <div
      ref={popupRef}
      className="fixed z-[1000] bg-white dark:bg-gray-800 rounded-full shadow-xl border p-2 flex gap-1 animate-scale-in"
      style={{ top: position.top - 50, left: position.left - 120 }}
    >
      {Object.entries(REACTIONS).map(([key, reaction]) => (
        <button
          key={key}
          onClick={() => !isReacting && onReactionSelect(key)}
          disabled={isReacting}
          className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:scale-110 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          title={reaction.label}
        >
          {isReacting ? (
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-2xl">{reaction.emoji}</span>
          )}
        </button>
      ))}
    </div>
  );
};

// ============================================
// REACTORS LIST COMPONENT
// ============================================

const ReactorsList = ({ reactors, onClose }) => {
  const [filter, setFilter] = useState('all');

  if (!reactors || reactors.length === 0) return null;

  const reactionTypes = ['all', ...new Set(reactors.map(r => r.reaction))];
  const filteredReactors =
    filter === 'all' ? reactors : reactors.filter(r => r.reaction === filter);
  const reactionCounts = {};
  reactors.forEach(r => {
    reactionCounts[r.reaction] = (reactionCounts[r.reaction] || 0) + 1;
  });

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="font-semibold">Reactions ({reactors.length})</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-3 border-b dark:border-gray-700 flex gap-2 overflow-x-auto">
          {reactionTypes.map(reaction => (
            <button
              key={reaction}
              onClick={() => setFilter(reaction)}
              className={`px-3 py-1 rounded-full text-sm transition-all whitespace-nowrap ${
                filter === reaction
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {reaction === 'all' ? (
                'All'
              ) : (
                <span className="flex items-center gap-1">
                  <span>{REACTIONS[reaction]?.emoji}</span>
                  <span>{REACTIONS[reaction]?.label}</span>
                  <span className="text-xs ml-1">
                    ({reactionCounts[reaction]})
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto max-h-[calc(80vh-120px)]">
          {filteredReactors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No reactions found
            </div>
          ) : (
            filteredReactors.map((reactor, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b dark:border-gray-700 last:border-0"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                  {reactor.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{reactor.name}</p>
                  <p className="text-xs text-gray-500">{reactor.email}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(reactor.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="text-2xl">
                  {REACTIONS[reactor.reaction]?.emoji}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMMENT SECTION COMPONENT
// ============================================

const CommentSection = ({ blogId }) => {
  const { isAdmin } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllComments, setShowAllComments] = useState(false);
  const [newComment, setNewComment] = useState({
    name: '',
    email: '',
    content: '',
  });
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState(null);
  const [copiedCommentId, setCopiedCommentId] = useState(null);
  const [reactionPosition, setReactionPosition] = useState(null);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [showReactors, setShowReactors] = useState(null);
  const [toast, setToast] = useState(null);
  const [isReacting, setIsReacting] = useState(false);
  const [pendingReactionEmoji, setPendingReactionEmoji] = useState(null);

  // CRITICAL FIX: Store reply form visibility independently for each comment
  const [replyFormsVisible, setReplyFormsVisible] = useState({});
  const [repliesVisibility, setRepliesVisibility] = useState({});
  const [replyContents, setReplyContents] = useState({});
  const [submittingReplies, setSubmittingReplies] = useState({});

  // ============================================
  // TOAST NOTIFICATION HELPERS
  // ============================================

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // ============================================
  // DATA FETCHING
  // ============================================

  const loadComments = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/blogs?action=get-comments&blogId=${blogId}`,
      );
      const data = await response.json();
      if (data.success) {
        setComments(data.comments);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
      showToast('Unable to load comments. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  const loadUserIdentifier = useCallback(async () => {
    let storedId = localStorage.getItem(`comment_user_${blogId}`);
    if (storedId) {
      setUserIdentifier(storedId);
      return storedId;
    }
    const userEmail = localStorage.getItem(`comment_user_email_${blogId}`);
    if (userEmail) {
      const generatedId = await generateUserIdentifier(userEmail);
      localStorage.setItem(`comment_user_${blogId}`, generatedId);
      setUserIdentifier(generatedId);
      return generatedId;
    }
    setUserIdentifier(null);
    return null;
  }, [blogId]);

  useEffect(() => {
    loadComments();
    loadUserIdentifier();
  }, [blogId, loadComments, loadUserIdentifier]);

  // ============================================
  // REACTION HANDLING WITH LOADING INDICATOR
  // ============================================

  const handleReaction = async (commentId, reaction) => {
    if (!userIdentifier) {
      showToast('Please add a comment before reacting.', 'warning');
      return;
    }

    const userName =
      localStorage.getItem(`comment_user_name_${blogId}`) ||
      newComment.name ||
      'Anonymous';
    const userEmail =
      localStorage.getItem(`comment_user_email_${blogId}`) ||
      newComment.email ||
      'anonymous@example.com';

    // Show loading indicator
    setIsReacting(true);
    setPendingReactionEmoji(REACTIONS[reaction]?.emoji);

    // Close popup immediately
    setReactionPosition(null);
    setSelectedCommentId(null);

    try {
      const response = await fetch('/api/blogs?action=react-to-comment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commentId,
          reaction,
          userIdentifier,
          userName,
          userEmail,
        }),
      });

      const data = await response.json();
      if (data.success) {
        await loadComments();
        showToast('Reaction added successfully!', 'success');
      } else {
        showToast(data.message || 'Failed to add reaction', 'error');
      }
    } catch (error) {
      console.error('Failed to add reaction:', error);
      showToast('Unable to add reaction. Please try again.', 'error');
    } finally {
      setIsReacting(false);
      setPendingReactionEmoji(null);
    }
  };

  // ============================================
  // COMMENT ACTIONS
  // ============================================

  const handleCopyComment = async (content, commentId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedCommentId(commentId);
      showToast('Comment copied to clipboard!', 'success');
      setTimeout(() => setCopiedCommentId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast('Unable to copy comment.', 'error');
    }
  };

  const showReactionPopup = (event, commentId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setReactionPosition({ top: rect.top, left: rect.left + rect.width / 2 });
    setSelectedCommentId(commentId);
  };

  const handleReactionSelect = reaction => {
    if (selectedCommentId) {
      handleReaction(selectedCommentId, reaction);
    }
  };

  const closeReactionPopup = () => {
    setReactionPosition(null);
    setSelectedCommentId(null);
  };

  // ============================================
  // INDEPENDENT REPLY FORM HANDLERS
  // ============================================

  const toggleReplyForm = commentId => {
    setReplyFormsVisible(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const closeReplyForm = commentId => {
    setReplyFormsVisible(prev => ({
      ...prev,
      [commentId]: false,
    }));
    setReplyContents(prev => ({
      ...prev,
      [commentId]: '',
    }));
  };

  const updateReplyContent = (commentId, content) => {
    setReplyContents(prev => ({
      ...prev,
      [commentId]: content,
    }));
  };

  // ============================================
  // INDEPENDENT REPLIES VISIBILITY HANDLERS
  // ============================================

  const toggleRepliesVisibility = commentId => {
    setRepliesVisibility(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  // ============================================
  // FIND ROOT COMMENT
  // ============================================

  const findRootComment = (comment, allComments) => {
    if (comment.parentId === 'root') return comment;

    const searchInList = (commentsList, targetId) => {
      for (const c of commentsList) {
        if (c.id === targetId) return c;
        if (c.replies && c.replies.length > 0) {
          const found = searchInList(c.replies, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const parentComment = searchInList(allComments, comment.parentId);
    if (parentComment) return findRootComment(parentComment, allComments);
    return null;
  };

  const canUserReplyToComment = (
    comment,
    allComments,
    currentUserIdentifier,
  ) => {
    if (!currentUserIdentifier) return false;
    if (isAdmin) return false;
    const rootComment = findRootComment(comment, allComments);
    if (!rootComment) return false;
    return rootComment.userIdentifier === currentUserIdentifier;
  };

  // ============================================
  // ADD COMMENT / REPLY
  // ============================================

  const handleAddComment = async e => {
    e.preventDefault();
    if (!newComment.name || !newComment.email || !newComment.content) {
      showToast('Please fill in all fields before posting.', 'warning');
      return;
    }

    if (!newComment.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      showToast('Please enter a valid email address.', 'warning');
      return;
    }

    const identifier = await generateUserIdentifier(newComment.email);
    setSubmitting(true);

    try {
      const endpoint = replyTo ? 'add-reply' : 'add-comment';
      const response = await fetch(`/api/blogs?action=${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blogId,
          parentId: replyTo ? replyTo.id : 'root',
          authorName: newComment.name,
          authorEmail: newComment.email,
          content: newComment.content,
          userIdentifier: identifier,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const storedIdentifier = data.userIdentifier || identifier;
        localStorage.setItem(`comment_user_${blogId}`, storedIdentifier);
        localStorage.setItem(`comment_user_email_${blogId}`, newComment.email);
        localStorage.setItem(`comment_user_name_${blogId}`, newComment.name);
        setUserIdentifier(storedIdentifier);

        setNewComment({ name: '', email: '', content: '' });
        setReplyTo(null);
        await loadComments();
        showToast(
          replyTo
            ? 'Reply posted successfully!'
            : 'Comment posted successfully!',
          'success',
        );
      } else {
        const errorMsg =
          data.details?.remaining !== undefined
            ? `You have ${data.details.remaining} comment${data.details.remaining !== 1 ? 's' : ''} remaining today.`
            : data.message;
        showToast(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      showToast('Unable to post. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (commentId, authorName) => {
    const replyContent = replyContents[commentId] || '';
    if (!replyContent.trim()) return;

    let identifier = localStorage.getItem(`comment_user_${blogId}`);
    const userEmail = localStorage.getItem(`comment_user_email_${blogId}`);
    const userName = localStorage.getItem(`comment_user_name_${blogId}`);

    if (!identifier && userEmail) {
      identifier = await generateUserIdentifier(userEmail);
    }

    // Set submitting state for this specific reply
    setSubmittingReplies(prev => ({ ...prev, [commentId]: true }));

    try {
      const response = await fetch('/api/blogs?action=add-reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blogId,
          parentId: commentId,
          authorName: userName || newComment.name || 'Guest',
          authorEmail: userEmail || newComment.email,
          content: replyContent,
          userIdentifier: identifier,
        }),
      });

      const data = await response.json();
      if (data.success) {
        updateReplyContent(commentId, '');
        closeReplyForm(commentId);
        await loadComments();
        showToast('Reply posted successfully!', 'success');
      } else {
        showToast(data.message || 'Failed to add reply', 'error');
      }
    } catch (error) {
      console.error('Failed to add reply:', error);
      showToast('Unable to post reply. Please try again.', 'error');
    } finally {
      setSubmittingReplies(prev => ({ ...prev, [commentId]: false }));
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const getAvatarColor = name => {
    const colors = [
      'bg-red-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const getReactionSummary = reactions => {
    if (!reactions) return null;
    const entries = Object.entries(reactions);
    if (entries.length === 0) return null;
    const topReaction = entries.reduce((a, b) => (a[1] > b[1] ? a : b));
    return {
      emoji: REACTIONS[topReaction[0]]?.emoji,
      count: entries.reduce((a, b) => a + b[1], 0),
    };
  };

  const visibleComments = showAllComments ? comments : comments.slice(0, 4);
  const hasMoreComments = comments.length > 4;

  // ============================================
  // COMMENT ITEM COMPONENT
  // ============================================

  const CommentItem = ({ comment, depth = 0, allComments = comments }) => {
    const isAdminComment =
      comment.isAdminReply || comment.authorEmail === 'admin@wdhejbul.com';
    const canReply = canUserReplyToComment(
      comment,
      allComments,
      userIdentifier,
    );
    const hasReplies = comment.replies && comment.replies.length > 0;

    // Use independent state for this comment
    const isReplyFormVisible = replyFormsVisible[comment.id] || false;
    const replyContent = replyContents[comment.id] || '';
    const isSubmittingReply = submittingReplies[comment.id] || false;
    const isRepliesVisible = repliesVisibility[comment.id] || false;

    const visibleReplies = isRepliesVisible
      ? comment.replies
      : comment.replies?.slice(0, 2) || [];
    const hasHiddenReplies = comment.replies?.length > 2;
    const reactionSummary = getReactionSummary(comment.reactions);
    const isCopied = copiedCommentId === comment.id;
    const isCurrentlyReacting = isReacting && selectedCommentId === comment.id;

    return (
      <div className={`relative ${depth > 0 ? 'ml-10 md:ml-12' : ''}`}>
        {/* Connecting line for nested comments */}
        {depth > 0 && (
          <>
            <div
              className="absolute left-[-24px] top-8 w-[24px] h-px"
              style={{ backgroundColor: 'var(--border)' }}
            />
            <div
              className="absolute left-[-24px] top-0 bottom-8 w-px"
              style={{ backgroundColor: 'var(--border)' }}
            />
          </>
        )}

        <div className="rounded-xl p-4 transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 group">
          <div className="flex gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {isAdminComment ? (
                <img
                  src={ADMIN_AVATAR}
                  alt="Admin"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-500"
                />
              ) : (
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${getAvatarColor(comment.authorName)}`}
                >
                  {comment.authorName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Comment Content */}
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`font-semibold text-sm ${isAdminComment ? 'text-blue-600 dark:text-blue-400' : ''}`}
                  >
                    {isAdminComment ? ADMIN_NAME : comment.authorName}
                  </span>
                  {isAdminComment && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      <FiShield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    {new Date(comment.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {comment.isEdited && (
                    <span
                      className="text-xs italic"
                      style={{ color: 'var(--muted)' }}
                    >
                      (edited)
                    </span>
                  )}
                </div>

                {/* Copy Button */}
                <button
                  onClick={() => handleCopyComment(comment.content, comment.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                  title="Copy comment"
                >
                  {isCopied ? (
                    <FiCheck className="w-3 h-3 text-green-500" />
                  ) : (
                    <FiCopy
                      className="w-3 h-3"
                      style={{ color: 'var(--muted)' }}
                    />
                  )}
                </button>
              </div>

              <p className="text-sm mb-2" style={{ color: 'var(--text)' }}>
                {comment.content}
              </p>

              {/* Action Buttons */}
              <div className="flex items-center gap-4 text-xs">
                {/* Reaction Button with Loading */}
                <button
                  onClick={e => showReactionPopup(e, comment.id)}
                  className="flex items-center gap-1 transition-all hover:opacity-70"
                  style={{ color: 'var(--muted)' }}
                  disabled={isCurrentlyReacting}
                >
                  {isCurrentlyReacting ? (
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  ) : pendingReactionEmoji &&
                    selectedCommentId === comment.id ? (
                    <span className="text-sm">{pendingReactionEmoji}</span>
                  ) : (
                    <FiThumbsUp className="w-3 h-3" />
                  )}
                  <span>
                    {isCurrentlyReacting
                      ? 'Adding...'
                      : pendingReactionEmoji && selectedCommentId === comment.id
                        ? ''
                        : 'React'}
                  </span>
                </button>

                {/* Reaction Summary */}
                {reactionSummary && (
                  <button
                    onClick={() => setShowReactors(comment)}
                    className="flex items-center gap-1 transition-all hover:opacity-70"
                    style={{ color: 'var(--muted)' }}
                  >
                    <span className="text-sm">{reactionSummary.emoji}</span>
                    <span>{reactionSummary.count}</span>
                  </button>
                )}

                {/* Reply Button - Independent toggle */}
                {canReply && (
                  <button
                    onClick={() => toggleReplyForm(comment.id)}
                    className="hover:underline transition-all flex items-center gap-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiCornerDownRight className="w-3 h-3" />
                    Reply
                  </button>
                )}

                {/* Show/Hide Replies Button - Independent toggle */}
                {hasReplies && (
                  <button
                    onClick={() => toggleRepliesVisibility(comment.id)}
                    className="hover:underline transition-all flex items-center gap-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    {isRepliesVisible ? (
                      <FiChevronUp className="w-3 h-3" />
                    ) : (
                      <FiChevronDown className="w-3 h-3" />
                    )}
                    {isRepliesVisible
                      ? 'Hide'
                      : `View ${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`}
                  </button>
                )}
              </div>

              {/* Reply Form - Independent visibility */}
              {isReplyFormVisible && (
                <div className="mt-3">
                  <textarea
                    value={replyContent}
                    onChange={e =>
                      updateReplyContent(comment.id, e.target.value)
                    }
                    placeholder={`Reply to ${comment.authorName}...`}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                    style={{
                      borderColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    rows="2"
                    disabled={isSubmittingReply}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() =>
                        handleReply(comment.id, comment.authorName)
                      }
                      disabled={!replyContent.trim() || isSubmittingReply}
                      className="px-3 py-1 rounded text-sm bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                    >
                      {isSubmittingReply ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FiSend className="w-3 h-3" />
                      )}
                      {isSubmittingReply ? 'Posting...' : 'Post Reply'}
                    </button>
                    <button
                      onClick={() => closeReplyForm(comment.id)}
                      className="px-3 py-1 rounded text-sm border hover:bg-black/5 dark:hover:bg-white/5"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Render Child Replies - Visibility controlled independently */}
        {hasReplies &&
          isRepliesVisible &&
          visibleReplies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              allComments={allComments}
            />
          ))}

        {/* "See More" Button for Hidden Replies */}
        {hasReplies && !isRepliesVisible && hasHiddenReplies && (
          <div className="ml-10 md:ml-12 mt-2">
            <button
              onClick={() => toggleRepliesVisibility(comment.id)}
              className="text-sm text-blue-500 hover:underline flex items-center gap-1"
            >
              <FiChevronDown className="w-4 h-4" />
              See {comment.replies.length - 2} more{' '}
              {comment.replies.length - 2 === 1 ? 'reply' : 'replies'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // LOADING STATE
  // ============================================

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div
      className="mt-8 pt-8 border-t"
      style={{ borderColor: 'var(--border)' }}
    >
      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Reaction Popup with loading state */}
      {reactionPosition && (
        <ReactionPopup
          position={reactionPosition}
          onReactionSelect={handleReactionSelect}
          onClose={closeReactionPopup}
          isReacting={isReacting}
        />
      )}

      {/* Reactors List Popup */}
      {showReactors && (
        <ReactorsList
          reactors={showReactors.reactors || []}
          onClose={() => setShowReactors(null)}
        />
      )}

      {/* Comments Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h2
          className="text-2xl font-bold flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <FiMessageCircle className="w-6 h-6" />
          Comments ({comments.length})
        </h2>
        {hasMoreComments && !showAllComments && (
          <button
            onClick={() => setShowAllComments(true)}
            className="flex items-center gap-1 text-sm hover:underline transition-all"
            style={{ color: 'var(--muted)' }}
          >
            See all comments ({comments.length})
            <FiChevronDown className="w-4 h-4" />
          </button>
        )}
        {showAllComments && (
          <button
            onClick={() => setShowAllComments(false)}
            className="flex items-center gap-1 text-sm hover:underline transition-all"
            style={{ color: 'var(--muted)' }}
          >
            Show less
            <FiChevronUp className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Comments List */}
      <div className="max-h-[600px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
        {visibleComments.length === 0 ? (
          <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
            <FiMessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          visibleComments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              depth={0}
              allComments={comments}
            />
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <div
        className="mt-8 pt-6 border-t"
        style={{ borderColor: 'var(--border)' }}
      >
        <h3
          className="text-lg font-semibold mb-4"
          style={{ color: 'var(--text)' }}
        >
          {replyTo ? `Replying to ${replyTo.authorName}` : 'Leave a Comment'}
        </h3>

        {replyTo && (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
            <span className="text-sm" style={{ color: 'var(--muted)' }}>
              Replying to:{' '}
              <span className="font-medium">{replyTo.authorName}</span>
            </span>
            <button
              onClick={() => setReplyTo(null)}
              className="text-sm hover:underline p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-all"
              style={{ color: 'var(--muted)' }}
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        )}

        <form onSubmit={handleAddComment} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text)' }}
              >
                Your Name *
              </label>
              <input
                type="text"
                placeholder="Enter your name"
                value={newComment.name}
                onChange={e =>
                  setNewComment({ ...newComment, name: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                required
                disabled={submitting}
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text)' }}
              >
                Your Email *
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                value={newComment.email}
                onChange={e =>
                  setNewComment({ ...newComment, email: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--text)' }}
            >
              Your Comment *
            </label>
            <textarea
              placeholder="Write your comment..."
              value={newComment.content}
              onChange={e =>
                setNewComment({ ...newComment, content: e.target.value })
              }
              rows={4}
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent transition-all resize-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              required
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            {submitting ? (
              <>
                <LoadingSpinner size="sm" />
                Posting...
              </>
            ) : (
              <>
                <FiSend className="w-4 h-4" />
                Post Comment
              </>
            )}
          </button>
        </form>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: var(--border);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--muted);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: var(--text);
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out forwards;
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CommentSection;
